/**
 * Formatting utility functions for WalkCompanion
 */

/**
 * Format number with commas
 */
export function formatNumber(value: number): string {
  return value.toLocaleString();
}

/**
 * Format number with specified decimal places
 */
export function formatNumberWithDecimals(value: number, decimalPlaces: number = 2): string {
  return value.toFixed(decimalPlaces);
}

/**
 * Format percentage
 */
export function formatPercentage(value: number, decimalPlaces: number = 1): string {
  return `${value.toFixed(decimalPlaces)}%`;
}

/**
 * Format currency
 */
export function formatCurrency(value: number, currency: string = 'USD'): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency
  }).format(value);
}

/**
 * Format large numbers (e.g., 1.2K, 1.5M)
 */
export function formatLargeNumber(value: number): string {
  if (value >= 1000000) {
    return `${(value / 1000000).toFixed(1)}M`;
  } else if (value >= 1000) {
    return `${(value / 1000).toFixed(1)}K`;
  } else {
    return value.toString();
  }
}

/**
 * Format steps
 */
export function formatSteps(steps: number): string {
  return formatLargeNumber(steps);
}

/**
 * Format distance
 */
export function formatDistance(meters: number, unit: 'm' | 'km' | 'mi' = 'km'): string {
  switch (unit) {
    case 'm':
      return `${Math.round(meters)}m`;
    case 'km':
      return `${(meters / 1000).toFixed(2)}km`;
    case 'mi':
      return `${(meters / 1609.34).toFixed(2)}mi`;
    default:
      return `${Math.round(meters)}m`;
  }
}

/**
 * Format time duration
 */
export function formatDuration(seconds: number): string {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = seconds % 60;

  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  } else if (minutes > 0) {
    return `${minutes}m ${remainingSeconds}s`;
  } else {
    return `${remainingSeconds}s`;
  }
}

/**
 * Format time duration in minutes only
 */
export function formatDurationMinutes(seconds: number): string {
  const minutes = Math.floor(seconds / 60);
  return `${minutes}m`;
}

/**
 * Format time duration in hours and minutes
 */
export function formatDurationHoursMinutes(seconds: number): string {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);

  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  } else {
    return `${minutes}m`;
  }
}

/**
 * Format calories
 */
export function formatCalories(calories: number): string {
  return `${Math.round(calories)} cal`;
}

/**
 * Format weight
 */
export function formatWeight(grams: number, unit: 'g' | 'kg' | 'lb' = 'kg'): string {
  switch (unit) {
    case 'g':
      return `${Math.round(grams)}g`;
    case 'kg':
      return `${(grams / 1000).toFixed(1)}kg`;
    case 'lb':
      return `${(grams / 453.592).toFixed(1)}lb`;
    default:
      return `${Math.round(grams)}g`;
  }
}

/**
 * Format pace (time per distance)
 */
export function formatPace(secondsPerMeter: number, distanceUnit: 'km' | 'mi' = 'km'): string {
  let paceSeconds: number;

  if (distanceUnit === 'km') {
    paceSeconds = secondsPerMeter * 1000; // seconds per km
  } else {
    paceSeconds = secondsPerMeter * 1609.34; // seconds per mile
  }

  const minutes = Math.floor(paceSeconds / 60);
  const seconds = Math.floor(paceSeconds % 60);

  return `${minutes}:${seconds.toString().padStart(2, '0')}/${distanceUnit}`;
}

/**
 * Format speed
 */
export function formatSpeed(metersPerSecond: number, unit: 'km/h' | 'mi/h' = 'km/h'): string {
  let speed: number;

  if (unit === 'km/h') {
    speed = metersPerSecond * 3.6; // km/h
  } else {
    speed = metersPerSecond * 2.237; // mi/h
  }

  return `${speed.toFixed(1)} ${unit}`;
}

/**
 * Format file size
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

/**
 * Format phone number
 */
export function formatPhoneNumber(phone: string): string {
  // Remove all non-digit characters
  const digits = phone.replace(/\D/g, '');

  // Format as (XXX) XXX-XXXX
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }

  // Return original if not 10 digits
  return phone;
}

/**
 * Format credit card number (masked)
 */
export function formatCreditCard(cardNumber: string): string {
  const digits = cardNumber.replace(/\D/g, '');
  if (digits.length >= 4) {
    return `**** **** **** ${digits.slice(-4)}`;
  }
  return cardNumber;
}

/**
 * Format initials from name
 */
export function formatInitials(name: string): string {
  return name
    .split(' ')
    .map(word => word.charAt(0).toUpperCase())
    .join('')
    .slice(0, 2);
}

/**
 * Format name (first letter capitalized)
 */
export function formatName(name: string): string {
  return name
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

/**
 * Format username (lowercase, no spaces)
 */
export function formatUsername(name: string): string {
  return name
    .toLowerCase()
    .replace(/\s+/g, '')
    .replace(/[^a-z0-9]/g, '');
}

/**
 * Format challenge title
 */
export function formatChallengeTitle(title: string): string {
  return title.charAt(0).toUpperCase() + title.slice(1);
}

/**
 * Format organization name
 */
export function formatOrganizationName(name: string): string {
  return name
    .split(' ')
    .map(word => {
      // Keep common abbreviations uppercase
      const abbreviations = ['LLC', 'Inc', 'Corp', 'Ltd', 'Co'];
      if (abbreviations.includes(word.toUpperCase())) {
        return word.toUpperCase();
      }
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(' ');
}

/**
 * Format team name
 */
export function formatTeamName(name: string): string {
  return name.charAt(0).toUpperCase() + name.slice(1);
}

/**
 * Format emoji with text
 */
export function formatEmojiText(emoji: string, text: string): string {
  return `${emoji} ${text}`;
}

/**
 * Format progress text
 */
export function formatProgressText(current: number, target: number, unit: string = ''): string {
  return `${formatNumber(current)}${unit} / ${formatNumber(target)}${unit}`;
}

/**
 * Format progress percentage
 */
export function formatProgressPercentage(current: number, target: number): string {
  const percentage = target > 0 ? (current / target) * 100 : 0;
  return `${percentage.toFixed(1)}%`;
}

/**
 * Format rank with suffix
 */
export function formatRank(rank: number): string {
  const suffix = getRankSuffix(rank);
  return `${rank}${suffix}`;
}

/**
 * Get rank suffix (1st, 2nd, 3rd, etc.)
 */
function getRankSuffix(rank: number): string {
  if (rank >= 11 && rank <= 13) {
    return 'th';
  }

  switch (rank % 10) {
    case 1:
      return 'st';
    case 2:
      return 'nd';
    case 3:
      return 'rd';
    default:
      return 'th';
  }
}

/**
 * Format points
 */
export function formatPoints(points: number): string {
  return `${formatNumber(points)} pts`;
}

/**
 * Format streak
 */
export function formatStreak(streak: number): string {
  if (streak === 1) {
    return '1 day';
  }
  return `${streak} days`;
}

/**
 * Format activity count with proper singular/plural
 */
export function formatActivityCount(count: number): string {
  if (count === 1) {
    return '1 activity';
  }
  return `${count} activities`;
}

/**
 * Format challenge count with proper singular/plural
 */
export function formatChallengeCount(count: number): string {
  if (count === 1) {
    return '1 challenge';
  }
  return `${count} challenges`;
}

/**
 * Format day count with proper singular/plural
 */
export function formatDayCount(count: number): string {
  if (count === 1) {
    return '1 day';
  }
  return `${count} days`;
}

/**
 * Format completion status
 */
export function formatCompletionStatus(isCompleted: boolean): string {
  return isCompleted ? 'Completed' : 'In Progress';
}

/**
 * Format challenge level
 */
export function formatChallengeLevel(level: string): string {
  return level.charAt(0).toUpperCase() + level.slice(1);
}

/**
 * Format challenge type
 */
export function formatChallengeType(type: string): string {
  return type.charAt(0).toUpperCase() + type.slice(1);
}

/**
 * Format metric unit
 */
export function formatMetricUnit(unit: string): string {
  const unitMap: Record<string, string> = {
    'steps': 'steps',
    'calories': 'cal',
    'minutes': 'min',
    'hours': 'hrs',
    'meters': 'm',
    'kilometers': 'km',
    'miles': 'mi',
    'glasses': 'glasses',
    'conversation': 'conversation',
    'meal': 'meal',
    'activity': 'activity',
    'connection': 'connection'
  };

  return unitMap[unit] || unit;
} 